import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movies';
import { getMovieById } from '@/utils/movie';

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const slides = heroSlides.map((s) => {
    const movie = getMovieById(s.id);
    return { ...s, movie };
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const go = (dir: number) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(delta) > 45) go(delta < 0 ? 1 : -1);
    touchX.current = null;
  };

  const active = slides[index];

  return (
    <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-primary-950">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={s.movie?.backdrop}
            alt={`${s.title} 剧照`}
            title={`${s.title} + 李采潭电影`}
            className={`w-full h-full object-cover object-top transition-transform duration-[7000ms] ${i === index ? 'scale-105' : 'scale-100'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/25"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-primary-950/30"></div>
        </div>
      ))}

      <div
        className="relative z-10 h-full w-full flex items-center px-4 md:px-8"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="max-w-2xl">
          <span key={`k-${index}`} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/90 text-primary-950 text-xs font-semibold animate-fade-in">
            <i className="ri-award-line"></i>
            {active.kicker}
          </span>
          <h1 key={`t-${index}`} className="font-heading text-4xl md:text-6xl font-bold text-background-50 mt-4 animate-fade-up">
            {active.title}
          </h1>
          <p key={`d-${index}`} className="text-background-200/90 text-sm md:text-base leading-relaxed mt-4 max-w-xl animate-fade-up">
            {active.description}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/40 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-grid-line"></i>
              浏览全部电影
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 backdrop-blur border border-background-50/30 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 backdrop-blur border border-background-50/30 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            aria-label={`切换到 ${s.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'}`}
          />
        ))}
      </div>
    </section>
  );
}